import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast, Toaster } from "sonner";
import { Database, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Setup() {
  const [secretCode, setSecretCode] = useState("");
  const [importing, setImporting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [results, setResults] = useState(null);

  const handleImport = async () => {
    if (secretCode !== "JardinViande2024Import!") {
      toast.error("Code secret incorrect");
      return;
    }

    setImporting(true);
    try {
      // Fetch data from preview
      toast.info("Téléchargement des données depuis la preview...");
      const previewUrl = "https://jardin-viande.preview.emergentagent.com/api/database/export";
      
      const exportResponse = await axios.get(previewUrl);
      const exportData = exportResponse.data;
      
      toast.info("Import en cours... (cela peut prendre quelques secondes)");
      
      // Import using secret key endpoint
      const importPayload = {
        secret: secretCode,
        collections: exportData.collections
      };
      
      const importResponse = await axios.post(`${API}/database/import-with-secret`, importPayload);
      
      setResults(importResponse.data.imported);
      setSuccess(true);
      toast.success("Import réussi !");
      
    } catch (error) {
      console.error("Error importing:", error);
      toast.error("Erreur: " + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Database className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Configuration initiale</CardTitle>
          <CardDescription>
            Importez les données depuis l'environnement de preview
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {!success ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code secret
                </label>
                <Input
                  type="password"
                  placeholder="Entrez le code secret"
                  value={secretCode}
                  onChange={(e) => setSecretCode(e.target.value)}
                  disabled={importing}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Le code secret vous a été fourni par le développeur
                </p>
              </div>
              
              <Button 
                onClick={handleImport} 
                disabled={importing || !secretCode}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Database className="h-4 w-4 mr-2" />
                    Importer les données
                  </>
                )}
              </Button>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <div className="flex gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>Attention :</strong> Cette action va remplacer toutes les données existantes par celles de la preview.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold text-green-800 text-lg">Import réussi !</h3>
                <p className="text-green-700 text-sm mt-1">
                  Toutes les données ont été importées avec succès.
                </p>
              </div>
              
              {results && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-700 mb-2">Résumé :</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Produits : {results.products || 0}</li>
                    <li>• Lots : {results.lots || 0}</li>
                    <li>• Promotions : {results.promotions || 0}</li>
                    <li>• Contenus : {results.site_content || 0}</li>
                    <li>• Utilisateurs admin : {results.admin_users || 0}</li>
                  </ul>
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Identifiants admin :</strong><br />
                  Utilisateur : <code className="bg-blue-100 px-1 rounded">Admin</code><br />
                  Mot de passe : <code className="bg-blue-100 px-1 rounded">HjgIPRWPnvI$r482</code>
                </p>
              </div>
              
              <Button 
                onClick={() => window.location.href = "/admin"}
                className="w-full"
              >
                Accéder à l'administration
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
